import dayjs from "dayjs";

import { MAX_FLIGHTS_NON_ADMIN } from "@/features/flight-estimator/_logic/constants";

import type { AirlineData } from "@/features/flight-estimator/_logic/types";

import { ANY_AIRLINES, MAX_SEARCH_DAYS_AHEAD } from "./constants";
import type { AirlinePreference, Cabin, FareQuote, Origin, RouteFlight, TripDates, Workspace } from "./types";
import { canPriceFares } from "./trip-dates";
import { cityKey, effectiveAirlines, groupName, originKey, placeFor, routeKey } from "./workspace";

/**
 * Why a route has no fare, in the words the comparison, the work log and the assistant use. A
 * search that failed to start, timed out or was refused reads differently from one that ran and
 * found nothing, so the planner is never told "no flights" when the search never looked.
 */
export const FARE_GAP = {
  /** The trip starts past the airlines' publish window; no search was run. */
  unpublished: "Fares aren't published this far ahead yet",
  /** The search was refused for too many searches and is being tried again. */
  rateLimitedRetrying: "Too many searches, trying again in a minute",
  /** The search was refused for too many searches and the retries are spent. */
  rateLimited: "Too many searches, try again in a few minutes",
  /** The search outlived the wait; whatever landed shows. */
  timedOut: "The flight search took too long",
  /** The search could not be started. */
  failed: "Couldn't start the flight search",
  /** The search ran and the pipeline returned no itinerary for the route. */
  noFlights: "No flights found",
} as const;

/**
 * Why a route has no fare when its city has no airport.
 * @param city - The city that resolved to no airport.
 * @returns The reason.
 * @throws Never.
 */
export const noAirportReason = (city: string): string => `No airport found for ${city}`;

/**
 * Whether airlines have published fares for these dates: the trip starts within
 * {@link MAX_SEARCH_DAYS_AHEAD} days. Past that, a search returns nothing for every route.
 * @param dates - Trip dates.
 * @param today - Today (for tests).
 * @returns True when a fare search can find anything.
 * @throws Never.
 */
export const faresPublished = (dates: TripDates, today = dayjs()): boolean =>
  dayjs(dates.start).diff(today.startOf("day"), "day") <= MAX_SEARCH_DAYS_AHEAD;

/**
 * One line for a set of unpriced routes: their shared reason when they have one ("Fares aren't
 * published this far ahead yet"), "No fares for 2 routes" when the search really found none,
 * and the reasons listed when they differ.
 * @param reasons - Each unpriced route's reason, as `FareQuote` and `RouteFare` carry it.
 * @returns The line, or null when nothing is unpriced.
 * @throws Never.
 */
export const describeFareGaps = (reasons: readonly string[]): string | null => {
  if (reasons.length === 0) return null;
  const routes = `${reasons.length} ${reasons.length === 1 ? "route" : "routes"}`;
  const distinct = [...new Set(reasons)];
  if (distinct.length > 1) return `No fares for ${routes}: ${distinct.join("; ")}`;
  return distinct[0] === FARE_GAP.noFlights ? `No fares for ${routes}` : distinct[0];
};

/** One traveler group sent to the flight pipeline. */
export interface PlannedGroup {
  /** The workspace origin it stands for, see `originKey`. */
  key: string;
  /** Group name in the pipeline: the team, or the home city when there is no team. */
  name: string;
  /** City or IATA code to fly from. */
  place: string;
  cabin: Cabin;
  count: number;
}

/** A search request: traveler groups, destination places and the airlines fares may use. */
export interface SearchPlan {
  groups: PlannedGroup[];
  destinations: string[];
  /** Normalized with `effectiveAirlines`. Plans saved before airline choices have none: any. */
  airlines?: AirlinePreference;
}

/**
 * The fare pipeline's airline settings for a plan's airline choice. With "only", the pipeline
 * asks for just those airlines and skips its Spirit and Frontier exclusion; otherwise it adds
 * the excluded ones to that default list.
 * @param airlines - The plan's airline choice; missing means any.
 * @returns `specific_airlines` and `exclude_airlines`, each `{ iata_code, name }`.
 * @throws Never.
 */
export const airlineSettings = (
  airlines: AirlinePreference | undefined
): { specific_airlines: AirlineData[]; exclude_airlines: AirlineData[] } => {
  const effective = effectiveAirlines(airlines ?? ANY_AIRLINES);
  const picked = effective.codes.map((code) => ({ iata_code: code, name: effective.names[code] ?? code }));
  return {
    specific_airlines: effective.mode === "only" ? picked : [],
    exclude_airlines: effective.mode === "exclude" ? picked : [],
  };
};

/** A started search, and the airport each city resolved to (by city key). */
export interface StartedSearch {
  token: string | null;
  airports: Record<string, string | null>;
}

/** One group-to-destination result, trimmed from the pipeline's reconstructed result. */
export interface SearchResult {
  groupName: string;
  departureCode: string;
  arrivalCode: string;
  travelClass: string;
  error: string | null;
  flight: {
    price: number;
    minutes: number;
    layovers: number;
    layoverMinutes: number;
    airline: string | null;
    flightNumber: string | null;
    airlineLogo: string | null;
    carbonKg: number;
    url: string | null;
    /** The chosen itinerary's details, read from the search's stored response once it completes. */
    details: FlightDetails | null;
  } | null;
}

/** One leg of the chosen outbound itinerary. */
export interface FlightLeg {
  airline: string | null;
  flightNumber: string | null;
  from: string | null;
  to: string | null;
  /** Local times as the airline publishes them, e.g. "2026-12-07 18:55". */
  departs: string | null;
  arrives: string | null;
  airplane: string | null;
}

/** What the pipeline knows about the flight behind a fare, beyond the price. */
export interface FlightDetails {
  legs: FlightLeg[];
  /** Airports of the connections, in order; empty for a direct flight. */
  via: string[];
  /** Cabin as the airline names it, e.g. "Economy". */
  cabin: string | null;
  /** Other itineraries the search found for this route. */
  otherOptions: number;
}

/** A poll of a running search. */
export interface SearchPoll {
  status: "searching" | "complete" | "error";
  progress: number;
  airportNames: Record<string, string>;
  results: SearchResult[];
}

/** A search the estimate has run (or is running) for one plan. */
export interface SearchEntry {
  plan: SearchPlan;
  started: StartedSearch | null;
  poll: SearchPoll | null;
  error: string | null;
}

/**
 * The flight search the workspace needs, if any: its traveler groups (largest first, capped so
 * routes stay within the pipeline's limit) and a signature that changes only when a new search
 * is needed. Headcounts are not part of it: fares are per traveler.
 * @param workspace - The workspace.
 * @returns The plan and its signature, or null until there are dates, home cities and destinations.
 * @throws Never.
 */
export const planSearch = (
  workspace: Workspace
): { plan: SearchPlan; signature: string; unchecked: Set<string> } | null => {
  if (!workspace.dates || workspace.origins.length === 0 || workspace.destinations.length === 0) return null;
  // Past or too-soon dates have no fares to find; the estimate asks for new dates instead. Dates
  // past the airlines' publish window have none either: no paid search, every route says why.
  if (!canPriceFares(workspace.dates) || !faresPublished(workspace.dates)) return null;
  const perDestination = Math.max(1, Math.floor(MAX_FLIGHTS_NON_ADMIN / workspace.destinations.length));
  const sorted = [...workspace.origins].sort((a, b) => b.count - a.count);
  const groups = sorted.slice(0, perDestination).map(
    (origin): PlannedGroup => ({
      key: originKey(origin),
      name: groupName(origin),
      place: placeFor(origin.city, workspace),
      cabin: origin.cabin ?? workspace.cabin,
      count: origin.count,
    })
  );
  const destinations = workspace.destinations.map((city) => placeFor(city, workspace));
  const airlines = effectiveAirlines(workspace.airlines);
  const signature = JSON.stringify({
    groups: groups.map(({ name, place, cabin }) => [name, cityKey(place), cabin]).sort(),
    destinations: destinations.map(cityKey).sort(),
    // Spelled out in a fixed order: dates read back from the database come with their keys
    // reordered, which would otherwise make the same plan look new and search it again.
    dates: { start: workspace.dates.start, end: workspace.dates.end },
    // Left out for any airline, so estimates saved before airline choices keep their fares.
    ...(airlines.mode === "any" ? {} : { airlines: [airlines.mode, ...airlines.codes] }),
  });
  return {
    plan: { groups, destinations, airlines },
    signature,
    unchecked: new Set(sorted.slice(perDestination).map(originKey)),
  };
};

/**
 * The search's result for one group and destination, if it has landed.
 * @param entry - The search.
 * @param origin - Home city or team.
 * @param destination - Destination city.
 * @param workspace - Supplies airport picks.
 * @returns The result, or null.
 * @throws Never.
 */
export const findResult = (
  entry: SearchEntry,
  origin: Origin,
  destination: string,
  workspace: Pick<Workspace, "airports">
): SearchResult | null => {
  const airports = entry.started?.airports ?? {};
  const from = airports[cityKey(placeFor(origin.city, workspace))];
  const to = airports[cityKey(placeFor(destination, workspace))];
  return (
    entry.poll?.results.find(
      (result) => result.groupName === groupName(origin) && result.departureCode === from && result.arrivalCode === to
    ) ?? null
  );
};

/**
 * The flight behind a priced result, in the shape the comparison and the chat use.
 * @param result - A priced search result.
 * @returns The flight summary.
 * @throws Never.
 */
export const routeFlight = (result: SearchResult): RouteFlight | null => {
  const flight = result.flight;
  if (!flight) return null;
  const details = flight.details;
  const firstLeg = details?.legs[0];
  const lastLeg = details?.legs[details.legs.length - 1];
  return {
    airline: flight.airline ?? firstLeg?.airline ?? null,
    flightNumber: flight.flightNumber ?? firstLeg?.flightNumber ?? null,
    airlineLogo: flight.airlineLogo,
    minutes: flight.minutes,
    stops: details ? details.via.length : flight.layovers,
    via: details?.via ?? [],
    departs: firstLeg?.departs ?? null,
    arrives: lastLeg?.arrives ?? null,
    cabin: details?.cabin ?? null,
    url: flight.url,
    otherOptions: details ? details.otherOptions : null,
  };
};

/**
 * Per-traveler fares for every origin and destination, from a search's results so far. A route
 * stays `loading` until the search has finished; only then is an unpriced route `missing` with the
 * reason, never a $0 fare. Dates past the airlines' publish window make every route `missing`
 * with that reason, with no search.
 * @param workspace - The workspace.
 * @param entry - The search for the workspace's current plan, if any.
 * @param unchecked - Origins left out of the search to stay within the route limit.
 * @returns Quotes keyed by `routeKey(originKey, destination)`.
 * @throws Never.
 */
export const quotesFromSearch = (
  workspace: Workspace,
  entry: SearchEntry | null,
  unchecked: ReadonlySet<string>
): Map<string, FareQuote> => {
  const quotes = new Map<string, FareQuote>();
  const unpublished = workspace.dates !== null && !faresPublished(workspace.dates);
  for (const origin of workspace.origins) {
    for (const destination of workspace.destinations) {
      const key = routeKey(originKey(origin), destination);
      if (unpublished) {
        quotes.set(key, { status: "missing", reason: FARE_GAP.unpublished });
        continue;
      }
      if (unchecked.has(originKey(origin))) {
        quotes.set(key, { status: "unchecked" });
        continue;
      }
      if (!entry || entry.error) {
        quotes.set(key, entry?.error ? { status: "missing", reason: entry.error } : { status: "loading" });
        continue;
      }
      const airports = entry.started?.airports;
      if (airports) {
        if (!airports[cityKey(placeFor(origin.city, workspace))]) {
          quotes.set(key, { status: "missing", reason: noAirportReason(origin.city) });
          continue;
        }
        if (!airports[cityKey(placeFor(destination, workspace))]) {
          quotes.set(key, { status: "missing", reason: noAirportReason(destination) });
          continue;
        }
      }
      const result = findResult(entry, origin, destination, workspace);
      if (result?.flight && result.flight.price > 0) {
        quotes.set(key, {
          status: "priced",
          usd: result.flight.price,
          originAirport: result.departureCode,
          destinationAirport: result.arrivalCode,
          flight: routeFlight(result),
        });
      } else if (entry.poll?.status === "complete" || entry.poll?.status === "error") {
        // Only a finished search can say a route has no fare. While it runs, the pipeline writes
        // rows for routes it hasn't priced yet, and those are still pending, not "No fare".
        quotes.set(key, { status: "missing", reason: result?.error ?? FARE_GAP.noFlights });
      } else {
        quotes.set(key, { status: "loading" });
      }
    }
  }
  return quotes;
};

/**
 * A finished search that already priced every route a plan needs: same dates, and at least its
 * groups and destinations. Removing a city or a home city then needs no new search, and undoing
 * it lands back on the original.
 * @param signature - The plan's signature, from {@link planSearch}.
 * @param entries - Searches run so far, by signature.
 * @returns That search, or null.
 * @throws Never.
 */
export const coveringSearch = (signature: string, entries: ReadonlyMap<string, SearchEntry>): SearchEntry | null => {
  type Parsed = {
    groups: string[][];
    destinations: string[];
    dates: { start: string; end: string };
    airlines?: string[];
  };
  const want = JSON.parse(signature) as Parsed;
  const groupKey = (group: string[]) => JSON.stringify(group);
  const airlinesKey = (parsed: Parsed) => JSON.stringify(parsed.airlines ?? null);
  for (const [other, entry] of entries) {
    if (entry.error || entry.poll?.status !== "complete") continue;
    const have = JSON.parse(other) as Parsed;
    if (have.dates.start !== want.dates.start || have.dates.end !== want.dates.end) continue;
    // Fares limited to other airlines are not these fares.
    if (airlinesKey(have) !== airlinesKey(want)) continue;
    const groups = new Set(have.groups.map(groupKey));
    const destinations = new Set(have.destinations);
    if (
      want.groups.every((group) => groups.has(groupKey(group))) &&
      want.destinations.every((city) => destinations.has(city))
    ) {
      return entry;
    }
  }
  return null;
};
